import { ITeam } from './teams';
import { Nullable } from './utils';

export const enum UserBillingPlan {
  BasicMonthly = 'basic-monthly',
  PremiumYearly = 'premium-yearly'
}

export const enum UserRole {
  admin = 'admin',
  manager = 'manager'
}

export const enum EUserJobFunction {
  Engineering = 'Engineering',
  Design = 'Design'
}

export interface UserSubscription {
  subscriptionId: string;
  planId: UserBillingPlan;
  isActive: boolean;
  quantity: number;
  quantityUsed: number;
  startDate: string;
  endDate: string;
  nextBillingAt: string;
  status: string;
  inTrial: boolean;
  trialStart: string;
  trialEnd: string;
  planName: string;
  remainingBillingCycles: Nullable<number>;
}

export interface UserLicense {
  subscription: UserSubscription;
  activatedOn: string;
  previousSubscription?: UserSubscription;
  orgCardStatus: boolean;
}

export interface IUser {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  profilePic: string | null;
  position?: string;
  isActive: boolean;
  jobFunction: string;
  upn: string | null;
}

export interface IPrivacySettings {
  privacy: string;
  locked: boolean;
  disabled: boolean;
}

export interface IMeetingPrivacySettings {
  externalMeetingsPrivacy: IPrivacySettings;
  internalMeetingsPrivacy: IPrivacySettings;
}

export interface IMeetingPrivacyAuditTrail {
  userInfo: IModifiedByUser;
  created: string;
}

export interface IUserSettingDetails {
  uuid: string;
  meetingPrivacySettings: IMeetingPrivacySettings;
  internalMeetingsPrivate: boolean;
  internalMeetingsPrimaryTeamsAccess: boolean;
  participantMeetingsPrivate: boolean;
  participantMeetingsPrimaryTeamsAccess: boolean;
  internalMeetingsTeamsWithAccess: ITeam[];
  externalMeetingsTeamsWithAccess: ITeam[];
  privacyAuditTrail: IMeetingPrivacyAuditTrail[]; // External meetings
  internalMeetingPrivacyAuditTrail: IMeetingPrivacyAuditTrail[]; // Internal meetings
}

export interface IMember {
  user: IUser;
  organization: string;
  uuid: string;
  active: boolean;
  position: string | null;
  status: string;
  role: {
    name: string;
    displayName: string;
    description: string;
    uuid: string;
    roleType: string;
  };
  primaryTeam: {
    name: string;
    uuid: string;
  };
  teams: {
    team: {
      name: string;
      uuid: string;
    };
    role: {
      name?: string;
      displayName?: string;
    };
  }[];
  settingDetails: IUserSettingDetails;
}

export interface IModifiedByUser {
  email: string;
  firstName: string;
  lastName: string;
  profilePic?: string;
  isActive: boolean;
}

export interface IModifiedByUserSnakeCase {
  email: string;
  first_name: string;
  last_name: string;
  profile_pic?: string;
  is_active: boolean;
}

export type IModifiedByUserCaseInsensitive =
  | IModifiedByUser
  | IModifiedByUserSnakeCase;

export type TUserOnboardingStepsKey =
  | 'gmail'
  | 'outlook'
  | 'calendar'
  | 'recording'
  | 'voiceprint'
  | 'crm';
export interface IUserOnboardingSteps {
  skipped: {
    [key in TUserOnboardingStepsKey]: boolean;
  };
  completed: {
    [key in TUserOnboardingStepsKey]: boolean;
  };
  '@version': number;
}
